/* ============================================================
   ENGRAVER'S LAB — engraving variant UI controller
   Progressive-enhancement layer over engraving-product-module.

   The module's add-to-cart + Drive pipeline are driven by the
   per-variant <input class="qty-input" data-variant> fields in its
   (now visually hidden) table. This controller adds a single-select
   variant button UI + one quantity stepper, and writes the chosen
   variant/quantity back into those native inputs — dispatching the
   same 'input' events the module already listens for. No module JS
   is modified; the upload/crop/Drive flow is completely untouched.
   ============================================================ */
(function () {
  'use strict';

  function initModule(root) {
    var qtyInputs = Array.prototype.slice.call(root.querySelectorAll('.qty-input[data-variant]'));
    if (!qtyInputs.length) return;

    var pills = Array.prototype.slice.call(root.querySelectorAll('[data-epm-vpill]'));
    var qtyValEl = root.querySelector('[data-epm-qty-value]');
    var minusBtn = root.querySelector('[data-epm-qty-minus]');
    var plusBtn = root.querySelector('[data-epm-qty-plus]');

    var activeId = null;
    var qty = 1;

    function fire(input) {
      input.dispatchEvent(new Event('input', { bubbles: true }));
    }

    // Write the current (activeId, qty) into the native inputs: active gets
    // the quantity, every other variant gets 0. Only dispatch on change.
    function apply() {
      qtyInputs.forEach(function (inp) {
        var target = (inp.dataset.variant === activeId) ? qty : 0;
        if ((parseInt(inp.value, 10) || 0) !== target) {
          inp.value = target;
          fire(inp);
        }
      });
    }

    // Forced version of apply() that always dispatches input events, even when
    // the underlying value hasn't changed. Used after image upload so the
    // module re-runs its update path and enables Add to Cart — covers the race
    // where the user uploads before our debounced auto-select has settled.
    function forceApply() {
      qtyInputs.forEach(function (inp) {
        var target = (inp.dataset.variant === activeId) ? qty : 0;
        inp.value = target;
        fire(inp);
      });
    }

    function paintPills() {
      pills.forEach(function (p) {
        var on = p.dataset.epmVariant === activeId;
        p.classList.toggle('is-active', on);
        p.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    }

    function selectVariant(id) {
      activeId = id;
      paintPills();
      apply();
      reconcile();
      refreshTint();
    }

    // Authoritatively sync the module's selection to exactly { activeId: qty }.
    //
    // Why this is needed: the module collapses our per-variant `input` events
    // through a single shared 300ms debounce, so when we switch variants (old→0,
    // new→qty) only ONE of those two updates survives — whichever fired last in
    // DOM order. That made the cart quantity drift up (switching right) or down
    // (switching left). Here we cancel the pending debounce and set the module's
    // selectedVariants map directly via its own methods, so the result is exact
    // and order-independent. No module JS is modified.
    function reconcile() {
      var inst = root.__epmInstance;
      if (!inst || !inst.selectedVariants || typeof inst.updateVariantSelection !== 'function') return;
      // Stop the in-flight debounced update from re-applying a stale (id, qty).
      if (inst.debouncedUpdateVariant && typeof inst.debouncedUpdateVariant.cancel === 'function') {
        try { inst.debouncedUpdateVariant.cancel(); } catch (e) {}
      }
      try {
        // Drop any selected variant that isn't the active one. Snapshot the keys
        // first with Array.from (a Map iterator is NOT array-like, so
        // Array.prototype.slice would yield an empty array) because
        // updateVariantSelection mutates the map as it deletes.
        Array.from(inst.selectedVariants.keys()).forEach(function (id) {
          if (id !== activeId) inst.updateVariantSelection(id, 0);
        });
        // Ensure the active variant carries exactly the current quantity.
        if (activeId) inst.updateVariantSelection(activeId, qty);
      } catch (e) { /* leave the input-event path as the fallback */ }
    }

    // If the monochrome preview is on, re-render so the tint matches the
    // newly-selected variant's colour. No-op if the module isn't ready or
    // monochrome is off. Reads the live module instance the module exposes.
    function refreshTint() {
      var inst = root.__epmInstance;
      if (inst && inst.isGrayscale && typeof inst.drawImage === 'function') {
        try { inst.drawImage(); } catch (e) {}
      }
    }

    function setQty(n) {
      qty = Math.max(1, n);
      if (qtyValEl) qtyValEl.textContent = qty;
      apply();
    }

    // Wire variant buttons
    pills.forEach(function (p) {
      p.addEventListener('click', function () {
        if (p.hasAttribute('disabled')) return;
        selectVariant(p.dataset.epmVariant);
      });
    });

    // Wire quantity stepper
    if (qtyValEl) qtyValEl.textContent = qty;
    if (minusBtn) minusBtn.addEventListener('click', function () { setQty(qty - 1); });
    if (plusBtn) plusBtn.addEventListener('click', function () { setQty(qty + 1); });

    // Initial auto-select — runs once the module has initialised (window load)
    // so the dispatched events register in the module's selectedVariants map.
    function autoSelect() {
      var firstId = null;
      if (pills.length) {
        for (var i = 0; i < pills.length; i++) {
          if (!pills[i].hasAttribute('disabled')) { firstId = pills[i].dataset.epmVariant; break; }
        }
      }
      if (!firstId) firstId = qtyInputs[0].dataset.variant; // default-variant products
      if (firstId) selectVariant(firstId);
    }

    if (document.readyState === 'complete') {
      autoSelect();
    } else {
      window.addEventListener('load', autoSelect, { once: true });
    }

    // When the cropper becomes visible (image just uploaded), re-fire the
    // active variant + qty so the module's selectedVariants Map is in sync
    // and Add to Cart enables immediately — no need to bump qty.
    var preview = root.querySelector('.image-preview-container');
    if (preview && 'MutationObserver' in window) {
      var mo = new MutationObserver(function () {
        var d = preview.style.display;
        if (d && d !== 'none' && activeId) forceApply();
      });
      mo.observe(preview, { attributes: true, attributeFilter: ['style'] });
    }
  }

  function boot() {
    document.querySelectorAll('.engraving-product-module').forEach(initModule);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
